// Formateadores compartidos por la página de Pedidos de venta y sus subtablas.

export function toISO(d: Date) { return d.toISOString().slice(0, 10) }

export function fmtDate(iso?: string) {
  if (!iso) return '—'
  const [y, m, d] = iso.split('-')
  return `${d}/${m}/${y}`
}

/** Cantidad completa con separador de millar (10.500, no «10,5k»). */
export function fmtN(n: number) {
  if (!n) return '—'
  return n.toLocaleString('es-ES', { maximumFractionDigits: 0 })
}

/** Como fmtN pero el cero se escribe "0", no "—".
 *  En un ratio "hecho / total" la raya se lee como «sin dato» cuando lo que
 *  quiere decir es «cero unidades». */
export function fmtQty(n: number) {
  if (!n) return '0'
  return fmtN(n)
}

/** Kilos con dos decimales. `null`/`undefined` → «—».
 *
 *  La raya aquí significa «el maestro de artículos no tiene el peso», no cero:
 *  al revés que en `fmtQty`, donde el cero sí se escribe. Un 0 en una columna de
 *  peso se lee como una medida y nadie lo pone en duda. */
export function fmtPeso(n?: number | null) {
  if (n == null) return '—'
  return n.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

/** Bultos. Mismo criterio de hueco que `fmtPeso`, pero sin decimales. */
export function fmtCajas(n?: number | null) {
  if (n == null) return '—'
  return n.toLocaleString('es-ES', { maximumFractionDigits: 0 })
}

export function fmtEur(n: number) {
  if (!n) return '—'
  return n.toLocaleString('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })
}

export function fmtPct(n: number) {
  if (!n && n !== 0) return '—'
  return `${n.toFixed(1)}%`
}

/** Colores de estado de línea, compartidos por las dos subtablas. */
export const ESTADO_COLOR: Record<string, string> = {
  'Pedido abierto': 'bg-sky-500/15 text-sky-400',
  'Entregado':      'bg-emerald-500/15 text-emerald-500',
  'Facturado':      'bg-purple-500/15 text-purple-400',
  'Cancelado':      'bg-red-500/15 text-red-500',
}

/** Fondo de la fila que casa con la búsqueda, SIN la barra.
 *
 *  Va aparte porque en una tabla con columnas congeladas cada celda fija repinta
 *  el fondo de su fila —si no, se transparenta lo que pasa por debajo— y la barra
 *  tiene que salir una sola vez, en el borde de la fila, no una por celda fija.
 *  Lo usan los reports; aquí basta con `filaResaltada`. */
export const fondoResaltado = (dark: boolean) => dark ? 'bg-sky-500/[0.12]' : 'bg-sky-50'

/** La barra izquierda de esa misma fila. */
export const barraResaltada = (dark: boolean) => dark
  ? 'border-l-2 border-l-sky-400'
  : 'border-l-2 border-l-sky-500'

/** Clases de la fila que casa con la búsqueda de artículo.
 *
 *  Fondo en toda la fila más una barra a la izquierda: en una carga con ocho
 *  artículos, dar con el que buscas a ojo es justo el problema que esto resuelve,
 *  y sólo el fondo se pierde cuando la fila está a mitad de una tabla larga.
 *
 *  Azul y no ámbar a propósito: el ámbar ya significa «sin facturar» en esta
 *  pantalla y dos ámbares distintos se leerían como lo mismo. */
export const filaResaltada = (dark: boolean) =>
  `${fondoResaltado(dark)} ${barraResaltada(dark)}`

/** Nº de columnas de la tabla principal (colSpan de las filas expandidas).
 *  Cuenta el canalón de marcas, que es una columna más de la rejilla.
 *
 *  Va a mano porque `COLS` vive en `PedidosVentasTabla` y éste lo importa quien
 *  pinta las subtablas: derivarlo crearía un ciclo. Quedarse corto NO da error
 *  —la subtabla desplegada abarca una columna menos y la rejilla se descuadra
 *  un poco—, así que lo amarra `totalCols.test.ts`. */
export const TOTAL_COLS = 14
/** Nº de columnas de la subtabla de líneas de Fabric (incluidas ORIGINAL y
 *  PICKING/SERVIDO, y el canalón de marcas). */
export const LINE_COLS = 17
